import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, TrendingUp, Sparkles, ArrowRight } from 'lucide-react';
import { format } from 'date-fns';
import { getArticleImage, handleImageError } from '../utils/imageUtils';

/**
 * MagazineHero - Premium editorial hero section
 * Large featured article on left, smaller articles + deal card on right
 * Approximately 500px height
 */
export const MagazineHero = ({ featuredArticles = [], featuredDeal = null }) => {
  const mainArticle = featuredArticles[0];
  const secondaryArticles = featuredArticles.slice(1, 3);

  if (!mainArticle) return null;

  return (
    <section className="bg-white border-b border-gray-100" data-testid="magazine-hero">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 min-h-[480px]">
          {/* Main Featured Article - Large Left */}
          <div className="lg:col-span-7">
            <Link 
              to={`/article/${mainArticle.slug}`} 
              className="group block relative h-full rounded-2xl overflow-hidden"
              data-testid="hero-main-article"
            >
              <div className="absolute inset-0">
                <img
                  src={getArticleImage(mainArticle)}
                  alt={mainArticle.title}
                  className="w-full h-full object-cover group-hover:scale-105 smooth-transition duration-700"
                  loading="eager"
                  onError={handleImageError}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/60 to-transparent"></div>
              </div>
              
              <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-8">
                <div className="flex items-center gap-2 mb-3">
                  <span className="bg-brand-red text-white px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-full">
                    {mainArticle.category_name}
                  </span>
                  {mainArticle.is_featured && (
                    <span className="bg-brand-yellow text-gray-900 px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-full flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" />
                      Featured
                    </span>
                  )}
                </div>
                
                <h1 className="text-2xl md:text-4xl lg:text-5xl font-playfair font-bold text-white mb-3 leading-tight drop-shadow-lg group-hover:text-brand-yellow smooth-transition">
                  {mainArticle.title}
                </h1>
                
                <p className="text-gray-200 text-base md:text-lg mb-4 line-clamp-2 max-w-2xl">
                  {mainArticle.excerpt}
                </p>
                
                <div className="flex items-center gap-4 text-sm text-gray-300">
                  <span className="flex items-center gap-1.5">
                    <Calendar className="w-4 h-4" />
                    {format(new Date(mainArticle.created_at), 'MMM dd, yyyy')}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Clock className="w-4 h-4" />
                    {mainArticle.reading_time} min read
                  </span>
                </div>
              </div>
            </Link>
          </div>

          {/* Right Side - Secondary Articles + Deal Card */}
          <div className="lg:col-span-5 flex flex-col gap-4">
            {/* Secondary Articles */}
            {secondaryArticles.map((article, index) => (
              <Link
                key={article.id}
                to={`/article/${article.slug}`}
                className="group flex-1 flex bg-white border border-gray-100 rounded-xl overflow-hidden hover:shadow-lg smooth-transition"
                data-testid={`hero-secondary-article-${index}`}
              >
                <div className="w-2/5 relative overflow-hidden">
                  <img
                    src={getArticleImage(article)}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 smooth-transition duration-500"
                    loading="lazy"
                    onError={handleImageError}
                  />
                </div>
                <div className="w-3/5 p-4 flex flex-col justify-center">
                  <span className="text-xs text-brand-red font-semibold uppercase tracking-wide mb-1">
                    {article.category_name}
                  </span>
                  <h3 className="font-playfair font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-brand-red smooth-transition leading-snug">
                    {article.title}
                  </h3>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <Clock className="w-3 h-3" />
                    {article.reading_time} min
                  </div>
                </div>
              </Link>
            ))}

            {/* Featured Deal Card */}
            {featuredDeal ? (
              <a
                href={featuredDeal.link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 bg-gradient-to-br from-brand-green to-emerald-700 rounded-xl p-5 text-white group hover-lift"
                data-testid="hero-deal-card"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-brand-yellow" />
                    <span className="text-xs font-bold uppercase tracking-wide text-gray-200">Today's Top Deal</span>
                  </div>
                  <span className="bg-brand-yellow text-gray-900 px-2 py-1 rounded-full text-xs font-black">
                    {featuredDeal.discount}
                  </span>
                </div>
                <h3 className="font-playfair font-bold text-lg mb-2 leading-tight">
                  {featuredDeal.title}
                </h3>
                <p className="text-gray-200 text-sm mb-3 line-clamp-2">
                  {featuredDeal.description}
                </p>
                <div className="flex items-center gap-1 text-brand-yellow font-semibold text-sm group-hover:gap-2 smooth-transition">
                  <span>Get Deal</span>
                  <ArrowRight className="w-4 h-4" />
                </div>
              </a>
            ) : (
              /* Fallback if no deal - promo card */
              <div className="flex-1 bg-gradient-to-br from-brand-green to-emerald-700 rounded-xl p-5 text-white flex flex-col justify-center">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-5 h-5 text-brand-yellow" />
                  <span className="text-xs font-bold uppercase tracking-wide text-gray-200">Hot Deals</span>
                </div>
                <h3 className="font-playfair font-bold text-lg mb-2">
                  Save Big on Top Brands
                </h3>
                <p className="text-gray-200 text-sm mb-3">
                  Discover exclusive coupons and discounts updated daily.
                </p>
                <Link 
                  to="/category/deals-offers"
                  className="inline-flex items-center gap-1 text-brand-yellow font-semibold text-sm hover:gap-2 smooth-transition"
                >
                  <span>Browse Deals</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default MagazineHero;
